import { useEffect, useRef, useState, type RefObject } from 'react';
import { NARROW_STAGE_QUERY } from '../../shared/narrow_stage.js';
import { guidedOpensOnMount, type GuidedOpen } from '../../shared/guided_open.js';
import { useMediaQuery } from '@voll-intelligence/ui-kit/hooks/use-media-query';
import type { PlannerLeg, PlannerTripType } from './planner_models.js';
import { firstPendingField, type LegField, type RouteEndpoints } from './leg_state.js';

type PlaceEndpoint = 'origin' | 'destination';

/** Qual praça o guia quer ABRIR agora, como continuação do gesto que respondeu
 *  o campo anterior: a de uma linha do multi (`legId`) ou a da fileira binária
 *  (`legId: null`). É um PEDIDO, não o estado aberto: o campo abre ao recebê-lo
 *  (`RouteField.advance`, a borda falso→verdadeiro) e avisa quando fecha
 *  (`settle`), zerando o pedido — o que deixa a mesma resposta repetida
 *  reabrir o campo seguinte de novo. */
type GuidedAdvance = { readonly legId: string | null; readonly field: PlaceEndpoint } | null;

/** O que o guia diz a UM campo de praça (`RouteField`). */
export type PlaceGuide = {
  readonly wanted: boolean;
  readonly advance: boolean;
  readonly onOpenChange: (open: boolean) => void;
};

type GuidedState = {
  readonly origin: string;
  readonly destination: string;
  readonly out: string | undefined;
  readonly ret: string | undefined;
  readonly activeTripType: PlannerTripType;
  readonly legs: readonly PlannerLeg[];
  readonly legIds: readonly string[];
  /** A linha do multi que pede a vez ao nascer (`seedEditingLeg`). */
  readonly editingLegId: string | null;
  readonly setLegFocus: (field: LegField | null) => void;
  readonly setDateOpenLegId: (id: string | null) => void;
};

/** O campo que a fileira larga abre ao NASCER: o primeiro pendente da fileira
 *  binária, ou, no multi, o da linha em edição (a primeira com pendência — a
 *  mesma régua do anel dela). */
function mountField(
  state: GuidedState,
  showTabs: boolean,
): { readonly legId: string | null; readonly field: LegField } | null {
  if (showTabs && state.activeTripType === 'multi_leg') {
    const leg = state.legs[state.legIds.indexOf(state.editingLegId ?? '')];
    const field = leg ? firstPendingField(leg) : null;
    return field && state.editingLegId !== null ? { legId: state.editingLegId, field } : null;
  }
  const field = pendingBinaryField(state, showTabs);
  return field ? { legId: null, field } : null;
}

/** O primeiro campo vazio da fileira BINÁRIA — a mesma régua do
 *  `firstPendingField` das linhas do multi, com a data completa só quando o
 *  modo fecha (ida e volta pede a volta). */
function pendingBinaryField(state: GuidedState, showTabs: boolean): LegField | null {
  if (!state.origin.trim()) return 'origin';
  if (!state.destination.trim()) return 'destination';
  const roundTrip = showTabs && state.activeTripType === 'round_trip';
  if (!state.out || (roundTrip && !state.ret)) return 'date';
  return null;
}

/**
 * O preenchimento GUIADO da página de Voos e do card da conversa
 * (`PlannerFormProps.guided`): o
 * primeiro campo vazio pede a vez (o anel de pendente — no binário por
 * `wanted`, no multi pelo `legFocus` de sempre) e RESPONDER um campo dentro do
 * painel dele abre o próximo pendente — De → Para → Quando, na fileira binária
 * e em cada linha do multi (e dali pra linha seguinte); com a viagem completa,
 * o foco pousa no Buscar — e o Confirmar das datas binárias já BUSCA (#4620,
 * a régua do `useGuidedFlow` do kit; na conversa, buscar é enviar a mensagem). Só a continuação de um gesto abre — a INVERSÃO das
 * pontas (escolher no Para a praça que já está no De, o botão inversor) só move
 * o anel, e adicionar um trecho só encadeia a origem — com UMA exceção: o
 * formulário nasce com o primeiro campo pendente já aberto e focado no palco
 * que o host pede (`opens`, ver `GuidedOpen`): a página só no largo, onde no
 * estreito a folha cobriria a página que já é o formulário; o card da conversa
 * também no estreito (#4879); o card remontado do histórico nunca. Fora do
 * layout aberto, congelado ou sem a prop, é inerte.
 */
export function useGuidedFill(p: {
  readonly guided: boolean | undefined;
  readonly openLayout: boolean;
  readonly readOnly: boolean;
  /** `PlannerFormProps.guidedOpen`. */
  readonly opens: GuidedOpen | undefined;
  /** `tripType` presente = as abas existem, e ida e volta pede a volta. */
  readonly initial: { readonly tripType?: PlannerTripType | undefined };
  readonly state: GuidedState;
  /** O popover/folha do QUANDO binário (controlado pelo form). */
  readonly setWhenOpen: (open: boolean) => void;
}) {
  const on = p.guided === true && p.openLayout && !p.readOnly;
  const showTabs = p.initial.tripType != null;
  // O palco estreito (os campos abrem em folha) — a MESMA régua do form; lida
  // aqui e não recebida porque o form está no teto de linhas.
  const narrow = useMediaQuery(NARROW_STAGE_QUERY);
  /* O campo que o form abre ao nascer — lido uma vez, no mount: é o estado
     inicial do pedido (as praças) ou do QUANDO (efeito abaixo), na fileira
     binária ou na linha do multi que pede a vez. */
  const [mount] = useState(() =>
    on && guidedOpensOnMount(p.opens, narrow) ? mountField(p.state, showTabs) : null,
  );
  const [advance, setAdvance] = useState<GuidedAdvance>(
    mount && mount.field !== 'date' ? { legId: mount.legId, field: mount.field } : null,
  );
  const { setWhenOpen } = p;
  const { setLegFocus, setDateOpenLegId } = p.state;
  useEffect(() => {
    if (mount?.field !== 'date') return;
    if (mount.legId === null) {
      setWhenOpen(true);
      return;
    }
    // Como no gesto no gatilho da linha: o anel acompanha o popover.
    setLegFocus('date');
    setDateOpenLegId(mount.legId);
  }, [mount, setWhenOpen, setLegFocus, setDateOpenLegId]);
  const ctaRef = useRef<HTMLButtonElement>(null);
  /* O QUANDO binário fechou por RESPOSTA (Confirmar, o toque do dia na
     somente ida) e não por Escape/clique fora: só aí o foco vai ao Buscar
     (`useFocusCtaOnComplete`). */
  const answered = useRef(false);
  const wanted = on ? pendingBinaryField(p.state, showTabs) : null;
  const settle = () => {
    setAdvance(null);
  };
  const advanceTo = (legId: string | null, field: LegField | null) => {
    if (field === 'origin' || field === 'destination') {
      setAdvance({ legId, field });
      return;
    }
    if (field === 'date') {
      if (legId === null) p.setWhenOpen(true);
      else p.state.setDateOpenLegId(legId);
      return;
    }
    ctaRef.current?.focus();
  };
  /* O primeiro campo VAZIO que o Buscar pergunta (#3844): na fileira binária a
     régua do anel; no multi a primeira linha com pendência. Só vazio conta —
     rota impossível e datas fora de ordem seguem travando com o aviso delas. */
  const pendingAsk = (multi: boolean): (() => void) | null => {
    if (!on) return null;
    if (!multi)
      return wanted
        ? () => {
            advanceTo(null, wanted);
          }
        : null;
    const index = p.state.legs.findIndex((leg) => firstPendingField(leg) !== null);
    const legId = p.state.legIds[index];
    const leg = p.state.legs[index];
    return legId !== undefined && leg
      ? () => {
          advanceTo(legId, firstPendingField(leg));
        }
      : null;
  };
  return {
    on,
    wanted,
    pendingAsk,
    ctaRef,
    answered,
    /** Confirmar as datas agora busca: a rota está dada e é viagem possível —
     *  o rótulo vira "Confirmar e buscar". */
    confirmSearches:
      on &&
      (wanted === null || wanted === 'date') &&
      p.state.origin.trim() !== p.state.destination.trim(),
    /** O Confirmar das datas binárias (saia do popover, rodapé da folha):
     *  `search` (ausente quando o pedido não está pronto) dispara. Não há
     *  praça pendente a abrir aqui: sem a rota, o calendário está travado. */
    confirmDates: (search: (() => void) | undefined) => {
      if (!on) return;
      answered.current = true;
      search?.();
    },
    /** O toque do dia na somente ida fecha o QUANDO como resposta. */
    answerDates: () => {
      answered.current = on;
    },
    /** As props de guia de um `RouteField`: o anel só na fileira binária (o
     *  multi já tem o dele pelo `legFocus`), a abertura por continuação e o
     *  aviso de fechamento que consome o pedido. */
    place: (legId: string | null, field: PlaceEndpoint): PlaceGuide => ({
      wanted: legId === null && wanted === field,
      advance: advance !== null && advance.legId === legId && advance.field === field,
      onOpenChange: (open) => {
        if (!open) settle();
      },
    }),
    /** As props do Buscar (#3844): com o pedido incompleto ele não fica travado
     *  sem dizer o quê — o clique abre o primeiro campo vazio, a mesma abertura
     *  da continuação do guia. Fora do guia, ou bloqueado por outra razão, segue
     *  o gate de `ready`. */
    searchCta: (multi: boolean, ready: boolean, submit: () => void) => {
      const ask = ready ? null : pendingAsk(multi);
      return { disabled: !ready && !ask, onClick: ask ?? submit };
    },
    /** Uma ponta da rota binária foi ESCOLHIDA (`swapped`: a escolha inverteu
     *  as pontas — só o anel anda). */
    afterRoutePick: (next: RouteEndpoints, swapped: boolean) => {
      if (!on || swapped) return;
      advanceTo(null, pendingBinaryField({ ...p.state, ...next }, showTabs));
    },
    /** Uma linha do multi foi editada e a vez foi pra `focus` (`nextFocus`):
     *  abrir o campo da vez, ou o Buscar com a viagem completa. */
    afterLegChange: (swapped: boolean, focus: { id: string | null; field: LegField | null }) => {
      if (!on || swapped) return;
      advanceTo(focus.id, focus.field);
    },
  };
}

export type PlannerGuide = ReturnType<typeof useGuidedFill>;

/**
 * O fecho do guia binário: o QUANDO fechou por resposta (Confirmar, o toque do
 * dia na somente ida — Escape e clique fora não contam) com o pedido pronto →
 * o foco pousa no Buscar. Efeito e
 * não handler porque quem fecha a camada (`PlannerWhenField.close`) devolve o
 * foco ao gatilho no mesmo gesto, e a última palavra tem de ser esta; a folha
 * (`WhenSheet`) já respeita um foco que se moveu.
 */
export function useFocusCtaOnComplete(
  guide: Pick<PlannerGuide, 'on' | 'ctaRef' | 'answered'>,
  whenOpen: boolean,
  ready: boolean,
): void {
  const wasOpen = useRef(whenOpen);
  const { on, ctaRef, answered: answeredRef } = guide;
  useEffect(() => {
    const closed = wasOpen.current && !whenOpen;
    wasOpen.current = whenOpen;
    if (!closed) return;
    if (on && answeredRef.current && ready) ctaRef.current?.focus();
    answeredRef.current = false;
  }, [on, ctaRef, answeredRef, whenOpen, ready]);
}

/** O `ref` do Buscar que o guia foca — tipado à parte pro `InlineSearchCta`. */
export type CtaRef = RefObject<HTMLButtonElement | null>;
